import axios from "axios";
import { Suspense, lazy, useEffect } from "react";
import { PARCA_SURESI_MS, yenilemeHakkiVar, zamanAsimli } from "@/lib/parcaYukle";
import { getCountry, guvenlikSayfasiAcikMi } from "@/country";
import { kaynagiYakala } from "@/lib/kaynak";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import UlkeSecimi from "@/components/UlkeSecimi";
import { AuthProvider } from "@/auth";
import Layout from "@/components/Layout";
// `/scan-cat` renders `ScanCatPhotoPicker` (Capacitor + HTML file-input
// fallback, iOS WebView safe).
//
// 31 Tem 2026: eski `ScanCat.jsx` (2812 satır) SİLİNDİ. "Diske duruyor,
// bir satırla geri alırız" diye tutuluyordu ama aylardır çağrılmıyordu ve
// tutulduğu sürece bakım borcu üretiyordu: `useCatDetector`'a bir bayrak
// eklerken onu bozmamak için varsayılanı gereksiz yere korumak zorunda
// kaldım — ölü bir dosya, yaşayan bir değişikliği kısıtladı.
//
// Geri almak hâlâ bir satır: `git show <bu commit>^:frontend/src/pages/ScanCat.jsx`.
// Git zaten diskin yaptığı işi yapıyor, üstelik geçmişiyle birlikte.
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages load when they are opened, not when the app boots.
//
// All 35 of these were imported eagerly, so every visitor downloaded the entire
// application before the landing page could paint: the MapLibre engine (~800 KB
// on its own), the 2,500-line camera flow, the charting library, and thirty
// screens they would never open. The app did not get slower because any one page
// got slower — it got slower because the bundle kept growing and everyone paid
// for all of it, up front, on a phone, on mobile data.
//
// Landing, Login and Register stay eager: they are the front door, and a spinner
// there costs more than the bytes it saves.
import Landing from "@/pages/Landing";
import Indir from "@/pages/Indir";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { premiumGizli } from "@/lib/magazaKurali";

// Yeni bir sürüm deploy edilince tarayıcıda kalan eski `main.js` artık var
// olmayan chunk hash'lerini istemeye çalışır ve "Loading chunk N failed" hatası
// verir. lazyRetry bunu yakalar: 10 sn'de en fazla bir kez sayfayı sessizce
// yeniler (taze index.html + yeni hash'ler gelir). Döngüye girmemek için zaman
// damgasıyla korunur — kalıcı bir bozuklukta tek sefer yenileyip hatayı gösterir.
function lazyRetry(factory) {
  return lazy(() =>
    // ⚠️ DONMA-2 (16 Eyl 2026) — ZAMAN AŞIMI EKLENDİ, DELİK BURADAYDI.
    //
    // Eski hâl yalnızca REDDEDİLEN yüklemeyi yakalıyordu (`factory().catch`).
    // Yükleme HİÇ CEVAP VERMEZSE — ne çözülür ne reddedilir — `catch` hiç
    // çalışmıyordu. React `Suspense`te sonsuza kadar bekliyor, React Router
    // beklerken ÖNCEKİ EKRANI göstermeye devam ediyor: uygulama donmuş
    // görünüyor ama canlı.
    //
    // Teşhisi kullanıcının gözlemi kapattı (16 Eylül):
    // *"ACİL butonu ve EN/TR çevirisi çalışıyor, harici her şey donuyor."*
    // O ikisi React Router'dan GEÇMİYOR — ACİL düz bir `<a href>` (tam sayfa
    // yenilemesi), EN/TR yalnızca durum değişimi. Geri kalan her şey rota
    // parçası indirmek zorunda. Yani ölü olan tek şey buydu.
    //
    // ⚠️ Sessiz bir asılma, hata veren bir asılmadan daha kötü: hatanın bir
    // yakalayıcısı vardı, sessizliğin yoktu.
    zamanAsimli(factory(), PARCA_SURESI_MS).catch((err) => {
      // ⚠️ SAYI SINIRI, SÜRE SINIRI DEĞİL. Önceki eşik "10 saniyede bir kez"
      // idi; 12 saniyelik zaman aşımıyla o pencere HER SEFERİNDE açık kalır ve
      // kalıcı bir bozuklukta uygulama 12 saniyede bir kendini yenileyip
      // dururdu — donmadan beter bir döngü. `yenilemeHakkiVar` en fazla iki
      // deneme veriyor, sonra hata yüzeye çıkıp açılış perdesinde anlatılıyor.
      if (yenilemeHakkiVar()) {
        window.location.reload();
        return new Promise(() => {}); // yeniden yükleme sırasında beklet
      }
      throw err;
    })
  );
}

// GECICI ROTA — yeni anasayfa. Hicbir yerden baglantisi YOK; sayfanin
// kendisi ozellik anahtaridir. `/` rotasina baglandigi COMMIT'te hem bu
// satir hem `Landing.jsx` silinir. Bkz. AnasayfaV2.jsx docstring'i.
const AnasayfaV2 = lazyRetry(() => import("@/pages/AnasayfaV2"));
// ACIL-01 (5 Eyl 2026): yeni Acil Bildir sihirbazi, gecici rotasinda.
// Eski dort sayfa (/injured-cat, /food-water-call, /danger-zone,
// /volunteer-call) BU AKIS onlari devraldigi COMMIT'te silinir ya da buraya
// yonlendirilir — iki giris bir gece bile yan yana durmaz.
const AcilBildir = lazyRetry(() => import("@/pages/AcilBildir"));
const KediBildir = lazyRetry(() => import("@/pages/KediBildir"));
const AcilVakalar = lazyRetry(() => import("@/pages/AcilVakalar"));
// ACIL-05 (10 Eyl 2026): acil merkezi. Kapisi Mahallem'in ucuncu karosu.
const AcilMerkezi = lazyRetry(() => import("@/pages/AcilMerkezi"));
// KAYIP-15 (5 Eyl 2026): AI karsilastirma ekrani. Uc ZATEN VARDI
// (POST /sightings/{id}/foto-karsilastir, KAYIP-13); bu yalnizca ekran.
const AiKarsilastirma = lazyRetry(() => import("@/pages/AiKarsilastirma"));
const KayipIlan = lazyRetry(() => import("@/pages/KayipIlan"));
const AramaAlani = lazyRetry(() => import("@/pages/AramaAlani"));
const IhbarEkle = lazyRetry(() => import("@/pages/IhbarEkle"));
const BenimMahallem = lazyRetry(() => import("@/pages/BenimMahallem"));
const MahalleListesi = lazyRetry(() => import("@/pages/MahalleListesi"));
// PLAN-06 ekran 7 — bolge detayi. Tasarimda haritanin ustunde ALT SAYFA;
// kendi rotasinda duruyor cunku alt sayfanin evi olan harita yeniden
// tasarimi Dilim M ve en sonda. Icerik ayni, kabuk sonra degisir.
const BolgeDetay = lazyRetry(() => import("@/pages/BolgeDetay"));
// PLAN-06 ekran 5 — Yapay Zeka Analizi. Tasarimda alt sayfa; kendi
// rotasinda cunku alt sayfanin evi olan harita Dilim M ve en sonda.
const AiAnaliz = lazyRetry(() => import("@/pages/AiAnaliz"));
// ANALIZ-8 (17 Eyl 2026): yeni Analiz panosu `/ai-analiz`; eski dört sekmeli sayfa `/ai-analiz/detay`.
const Analiz = lazyRetry(() => import("@/pages/Analiz"));
// ÖNCELİK-1 (18 Eyl 2026): Analiz'deki "Şu anda önemli 3 konu → Tümünü gör" buraya açılıyor.
const Oncelikler = lazyRetry(() => import("@/pages/Oncelikler"));
// SON7-1 (16 Eyl 2026): Mahallem → Son 7 Gün kartının detay sayfası.
const Son7Gun = lazyRetry(() => import("@/pages/Son7Gun"));
const GunDetay = lazyRetry(() => import("@/pages/GunDetay"));
const BolgeAnalizi = lazyRetry(() => import("@/pages/BolgeAnalizi"));
const ForgotPassword = lazyRetry(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"));
const CatList = lazyRetry(() => import("@/pages/CatList"));
const Ara = lazyRetry(() => import("@/pages/Ara"));
const CatVaccinations = lazyRetry(() => import("@/pages/CatVaccinations"));
const LostCat = lazyRetry(() => import("@/pages/LostCat"));
// KAYIP-1: iki adimli kayip ilani ekrani. Eski tek sayfali kip
// (`/emergency-report?category=lost`) DURUYOR — paylasilmis baglantiyla
// gelen kimse bos ekrana dusmesin.
const KayipBildir = lazyRetry(() => import("@/pages/KayipBildir"));
// KAYIP-2: kayip ilanlari LISTESI. 9 Eyl gecesine kadar bu liste hic
// yazilmamisti; tek gosteren yer eski `Sightings.jsx`ti ve ona 14 kapi
// bakiyordu. Eski rota SILINMEDI (paylasilmis baglantilar).
const KayipIlanlari = lazyRetry(() => import("@/pages/KayipIlanlari"));
// LANDING-1 (23 Eyl): Neo reklam sayfalari — girissiz, kendi kabuklari.
const KayipKediLanding = lazyRetry(() => import("@/pages/KayipKediLanding"));
const MahalleLanding = lazyRetry(() => import("@/pages/MahalleLanding"));
const FeedingDetail = lazyRetry(() => import("@/pages/FeedingDetail"));
const CatDetail = lazyRetry(() => import("@/pages/CatDetail"));
const CatEdit = lazyRetry(() => import("@/pages/CatEdit"));
const AddCat = lazyRetry(() => import("@/pages/AddCat"));
const AIIdentify = lazyRetry(() => import("@/pages/AIIdentify"));
const MapView = lazyRetry(() => import("@/pages/MapView"));
const Sightings = lazyRetry(() => import("@/pages/Sightings"));
const EmergencyDetail = lazyRetry(() => import("@/pages/EmergencyDetail"));
const EmergencyReport = lazyRetry(() => import("@/pages/EmergencyReport"));
const VolunteerCall = lazyRetry(() => import("@/pages/VolunteerCall"));
const OdulAvcisi = lazyRetry(() => import("@/pages/OdulAvcisi"));
const FoodWaterCall = lazyRetry(() => import("@/pages/FoodWaterCall"));
const FoodWaterCallDetail = lazyRetry(() => import("@/pages/FoodWaterCallDetail"));
const AcilKayitDetay = lazyRetry(() => import("@/pages/AcilKayitDetay"));
const Kaydettiklerim = lazyRetry(() => import("@/pages/Kaydettiklerim"));
const InjuredCat = lazyRetry(() => import("@/pages/InjuredCat"));
const DangerZone = lazyRetry(() => import("@/pages/DangerZone"));
const Volunteers = lazyRetry(() => import("@/pages/Volunteers"));
const Messenger = lazyRetry(() => import("@/pages/Messenger"));
const MesajlarAna = lazyRetry(() => import("@/pages/MesajlarAna"));
const MesajIstekleri = lazyRetry(() => import("@/pages/MesajIstekleri"));
const GrupSohbeti = lazyRetry(() => import("@/pages/GrupSohbeti"));
const KonumPaylas = lazyRetry(() => import("@/pages/KonumPaylas"));
const KonusmaDetay = lazyRetry(() => import("@/pages/KonusmaDetay"));
const MedyaPaylas = lazyRetry(() => import("@/pages/MedyaPaylas"));
const Notifications = lazyRetry(() => import("@/pages/Notifications"));
const Feeding = lazyRetry(() => import("@/pages/Feeding"));
const AddFeeding = lazyRetry(() => import("@/pages/AddFeeding"));
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const ProfileEdit = lazyRetry(() => import("@/pages/ProfileEdit"));
const HowItWorks = lazyRetry(() => import("@/pages/HowItWorks"));
const Help = lazyRetry(() => import("@/pages/Help"));
const About = lazyRetry(() => import("@/pages/About"));
const NotFound = lazyRetry(() => import("@/pages/NotFound"));
const MyPetCats = lazyRetry(() => import("@/pages/MyPetCats"));
const MyPetCatDetail = lazyRetry(() => import("@/pages/MyPetCatDetail"));
const Leaderboard = lazyRetry(() => import("@/pages/Leaderboard"));
const Premium = lazyRetry(() => import("@/pages/Premium"));
const PremiumSuccess = lazyRetry(() => import("@/pages/PremiumSuccess"));
const PremiumCancel = lazyRetry(() => import("@/pages/PremiumCancel"));
const PremiumLounge = lazyRetry(() => import("@/pages/PremiumLounge"));
const LiveIdentify = lazyRetry(() => import("@/pages/LiveIdentify"));
const ScanCatPhotoPicker = lazyRetry(() => import("@/pages/ScanCatPhotoPicker"));
const KediKimligi = lazyRetry(() => import("@/pages/KediKimligi"));
// Ölçüm aracı, kullanıcı özelliği değil. Menüde YOK — adresi bilen admin
// açar. Sunucu tarafında da ayrıca admin kapısı var; menüde olmaması
// gizlilik değil sadelik gerekçesi.
const AciEtiketle = lazyRetry(() => import("@/pages/AciEtiketle"));
// Kedi Kimliği'nden gelen üç kareyle açılan kayıt sayfası. `/scan-cat`
// sihirbazına DOKUNULMUYOR — o olduğu gibi duruyor; bu ayrı bir rota.
const KediProfiliOlustur = lazyRetry(() => import("@/pages/KediProfiliOlustur"));

/** Alt menüden ya da Kedi Kimliği'nden gelen kareleri sayfaya taşır.
 *
 * `ilkKare` alt menüden beri var. `ekKareler` + `ilkKareAcisi` 1 Ağu'da
 * eklendi: kullanıcı Kedi Kimliği'nde üç kare çekip "farklı kedi" deyince
 * üçü de çöpe gidiyor, hepsini yeniden çekmesi gerekiyordu. Kare yoksa
 * davranış birebir aynı. */
function ScanCatRotasi() {
  const konum = useLocation();
  return (
    <ScanCatPhotoPicker
      initialPhoto={konum.state?.ilkKare || null}
      ekKareler={konum.state?.ekKareler || []}
      ilkKareAcisi={konum.state?.ilkKareAcisi || "front"}
    />
  );
}
const ApplyVolunteer = lazyRetry(() => import("@/pages/ApplyVolunteer"));
const AdminApplications = lazyRetry(() => import("@/pages/AdminApplications"));
const AdminEmergencies = lazyRetry(() => import("@/pages/AdminEmergencies"));
const PhotoMonthly = lazyRetry(() => import("@/pages/PhotoMonthly"));
const Badges = lazyRetry(() => import("@/pages/Badges"));
const CatSafety = lazyRetry(() => import("@/pages/CatSafety"));
const IncidentFeedback = lazyRetry(() => import("@/pages/IncidentFeedback"));
const ObservationHouse = lazyRetry(() => import("@/pages/ObservationHouse"));
const PrivacyPolicy = lazyRetry(() => import("@/pages/PrivacyPolicy"));
const Terms = lazyRetry(() => import("@/pages/Terms"));
const CommunityRules = lazyRetry(() => import("@/pages/CommunityRules"));
const UserProfile = lazyRetry(() => import("@/pages/UserProfile"));
const PawFriends = lazyRetry(() => import("@/pages/PawFriends"));
const TanittiginKediler = lazyRetry(() => import("@/pages/TanittiginKediler"));
const Katkilarim = lazyRetry(() => import("@/pages/Katkilarim"));
const PatiAgi = lazyRetry(() => import("@/pages/PatiAgi"));
const MahalleRapor = lazyRetry(() => import("@/pages/MahalleRapor"));
const Gorevler = lazyRetry(() => import("@/pages/Gorevler"));
const GorevListesi = lazyRetry(() => import("@/pages/GorevListesi"));
const GorevDetay = lazyRetry(() => import("@/pages/GorevDetay"));
const GorevYeni = lazyRetry(() => import("@/pages/GorevYeni"));
const GorevSohbet = lazyRetry(() => import("@/pages/GorevSohbet"));
const GorevEtki = lazyRetry(() => import("@/pages/GorevEtki"));
const AktifGorevler = lazyRetry(() => import("@/pages/AktifGorevler"));
const Gruplar = lazyRetry(() => import("@/pages/Gruplar"));
const GrupYeni = lazyRetry(() => import("@/pages/GrupYeni"));
const GrupDetay = lazyRetry(() => import("@/pages/GrupDetay"));



/** Sayfa parçası yüklenirken gösterilir.
 *
 * SESSİZ tutuyoruz (24 Tem 2026): eskiden ekranın %70'ini kaplayan boş bir
 * alan + spinner çiziyordu; sayfa geçişlerinde "arada bir sayfa belirip
 * kaybolmuş" gibi görünüyordu. Artık hiçbir şey çizmiyor — chunk'lar küçük,
 * geçiş anında sayfa yerinde kalır, hazır olunca yenisi gelir. Ekran
 * okuyucular için görünmez bir durum metni bırakıldı. */
/**
 * Rota değiştiğinde sayfayı başa al.
 *
 * Tek sayfa uygulamalarında tarayıcı, bağlantıya basıldığında kaydırma
 * konumunu SIFIRLAMAZ: A sayfasında aşağı kaydırıp B'ye geçtiğinde pencere
 * hâlâ aşağıda kalır, B'nin üstü görünmez ve sayfa "altı gitmiş" gibi durur.
 *
 * Ama her durumda başa almak da yanlış olurdu: GERİ tuşuna basıldığında
 * tarayıcının eski konumu geri yüklemesi İSTENEN davranıştır — uzun kedi
 * listesinde 40. kediye kadar inip bir profile girip geri döndüğünde en başa
 * fırlamak istemezsin.
 *
 * Bu yüzden yalnızca ileri yönlü gezinmede (PUSH/REPLACE) başa alıyoruz,
 * geri/ileri (POP) durumunda tarayıcıya karışmıyoruz.
 */
// B.3 — kaynak, uygulamanin ACILDIGI adresten okunuyor.
//
// Modul duzeyinde, cizimden ONCE calisiyor: bir reklam tiklamasi her zaman
// yeni bir sayfa yuklemesidir, yani ilk URL dogru yerdir. Cizim yoluna hic
// dokunmuyor -- bugun (3 Eyl) cizim yolunda yapilan bir hata siteyi 30
// dakika dusurdu; bu is icin oraya girmeye gerek yok.
//
// Depolama kapaliysa ya da URL'de kaynak yoksa sessizce hicbir sey yapmaz.
kaynagiYakala();

function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

function RouteFallback() {
  // Eskiden bu sr-only idi: sayfa yuklenirken icerik alani TAMAMEN bos kaliyordu.
  // ACIL dugmesi ve alt menu position:fixed oldugu icin ekranda kaliyor, arada
  // kocaman beyaz bir dikdortgen olusuyordu — kullanici bunu "uygulama coktu"
  // diye okuyor. Gorunur bir iskelet, ayni bekleyisi "yukleniyor" diye anlatir.
  return (
    <div className="px-4 py-6 space-y-3" role="status" aria-live="polite">
      <span className="sr-only">Yükleniyor…</span>
      <div className="h-24 rounded-2xl bg-stone-100 animate-pulse" />
      <div className="h-40 rounded-2xl bg-stone-100 animate-pulse" />
      <div className="h-40 rounded-2xl bg-stone-100 animate-pulse" />
    </div>
  );
}

// Her /api isteğine kullanıcının ülkesini ekle (X-Country). Backend şehir
// listesini, fiyatı ve fotoğraf R2 bucket'ını buna göre seçer. Türkiye'de
// "tr" gider ve davranış hiç değişmez; straypaw.ai'de domain varsayılanı (id)
// ya da kullanıcı seçimi gider. Bir kez kurulur (modül seviyesi).
let _xCountryHooked = false;
if (!_xCountryHooked) {
  _xCountryHooked = true;
  axios.interceptors.request.use((config) => {
    try {
      const url = config.url || "";
      if (url.includes("/api")) {
        config.headers = config.headers || {};
        config.headers["X-Country"] = getCountry();
      }
    } catch (e) {
      /* sessiz — başlık eklenemezse backend varsayılana düşer */
    }
    return config;
  });
}

/**
 * TUR-9: ülke kapısı. Yeni kullanıcıya bir kez ülke sorar; mevcut kullanıcıya
 * hiç görünmez (bkz. UlkeSecimi.zatenBiliniyor).
 *
 * AuthProvider'dan SONRA yazılıyor — yani DOM'da daha sonra. Açılış turu
 * (IlkAcilisTuru) da tam ekran ve aynı z katmanında; ülke sorusu ondan ÖNCE
 * cevaplanmalı, çünkü turun metinleri ülkeye göre değişen yüze ait. DOM'da
 * sonra gelen üstte boyandığı için sıra bilinçli.
 */
function UlkeKapisi() {
  // Dil BILEREK gecilmiyor: ekran kendi dilini cihazdan aliyor. Uygulamanin
  // dili getCountry()'den turuyor ve ulke secilene kadar "tr" — o dili buraya
  // versek Endonezyalıya soruyu Turkce sorardik. Bkz. UlkeSecimi.cihazDili.
  return <UlkeSecimi />;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary scope="app-root">
          <Layout>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* ⚠️ GEÇİCİ — cihaz denemesi. Ana kapı yeni anasayfaya
                  bağlandı. `Layout.jsx`teki `V2_ROTALARI` listesine de "/"
                  eklendi; ikisi BİRLİKTE değişmek zorunda
                  (`Layout.kabuk.test.js` bunu ölçüyor). Kalıcı geçiş,
                  `Landing.jsx`in SİLİNDİĞİ commit'te olur. */}
              <Route path="/" element={<AnasayfaV2 />} />
              <Route path="/v2" element={<AnasayfaV2 />} />
              {/* Alt barın orta düğmesinin açtığı SEÇİM ekranı (pano Ekran 1).
                  Hedef `lib/altBar.js`teki ORTA_DUGME sabitinden okunur —
                  bu rota ile o sabit BİRLİKTE değişir. */}
              <Route path="/kedi-bildir" element={<KediBildir />} />
              <Route path="/acil-bildir" element={<AcilBildir />} />
              <Route path="/acil-vakalar" element={<AcilVakalar />} />
              <Route path="/acil-merkezi" element={<AcilMerkezi />} />
              <Route path="/kayip/:id" element={<KayipIlan />} />
              <Route path="/kayip/:id/ai-karsilastirma" element={<AiKarsilastirma />} />
              <Route path="/kayip/:id/arama-alani" element={<AramaAlani />} />
              <Route path="/kayip/:id/ihbar" element={<IhbarEkle />} />
              <Route path="/mahallem" element={<BenimMahallem />} />
              {/* SON7-1: kapısı Mahallem'deki Son 7 Gün kartı. */}
              <Route path="/mahallem/son-7-gun" element={<Son7Gun />} />
              <Route path="/mahallem/gun/:tarih" element={<GunDetay />} />
              <Route path="/mahallem/bolge-analizi" element={<BolgeAnalizi />} />
              {/* MAHALLE-1 K-2: Keşfet → "Tümünü gör" buraya açılıyor. */}
              <Route path="/mahalleler" element={<MahalleListesi />} />
              <Route path="/bolge/:yer" element={<BolgeDetay />} />
              <Route path="/ai-analiz" element={<Analiz />} />
              <Route path="/oncelikler" element={<Oncelikler />} />
              <Route path="/ai-analiz/detay" element={<AiAnaliz />} />
              {/* Reklamdan gelen kisiyi cihazina gore magazaya birakir. */}
              <Route path="/indir" element={<Indir />} />
              <Route path="/download" element={<Indir />} />
              <Route path="/get" element={<Indir />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* ARA — kedi veritabanina giden yol (PLAN-12). Anasayfadaki
                  arama cubugu artik bir dugme ve burayi aciyor. */}
              <Route path="/ara" element={<Ara />} />
              <Route path="/cats" element={<CatList />} />
              <Route path="/cat-vaccinations" element={<CatVaccinations />} />
              <Route path="/lost-cat" element={<LostCat />} />
              <Route path="/kayip-bildir" element={<KayipBildir />} />
              <Route path="/kayip-ilanlari" element={<KayipIlanlari />} />
              <Route path="/kayip-kedi" element={<KayipKediLanding />} />
              <Route path="/mahalle/:ad" element={<MahalleLanding />} />
              <Route path="/cats/new" element={<AddCat />} />
              <Route path="/cats/:id" element={<CatDetail />} />
              {/* KEDİPROFİL-2 (18 Eyl 2026): hedef panoda bulunmayan ağır
                  özellikler ana profil akışına eklenmez. ⋮ menüsü onları
                  kayıpsız biçimde kendi ekranlarına açar. Statik rotalar
                  genel `:sekme` rotasından önce görünür tutuluyor. */}
              <Route path="/cats/:id/bilgiler" element={<CatDetail bolum="bilgiler" />} />
              <Route path="/cats/:id/anilar" element={<CatDetail bolum="anilar" />} />
              <Route path="/cats/:id/saglik" element={<CatDetail bolum="saglik" />} />
              <Route path="/cats/:id/yonetim" element={<CatDetail bolum="yonetim" />} />
              {/* PLAN-14 Dilim A — kedi profilinin sekmeleri. Sıra ve etiket
                  `lib/kediSekmeleri.js`te; bilinmeyen bir parça (ör. eski bir
                  bağlantı) CatDetail içinde köke yönlendirilir. Statik parça
                  dinamikten önce eşleştiği için `/cats/:id/edit` bu rotaya
                  DÜŞMEZ — `Layout.kediProfiliMi` de onu kabuksuz saymaz. */}
              <Route path="/cats/:id/:sekme" element={<CatDetail />} />
              <Route path="/cats/:id/edit" element={<CatEdit />} />
              <Route path="/identify" element={<AIIdentify />} />
              <Route path="/identify/live" element={<LiveIdentify />} />
              {/* Alt menüdeki yapay zeka düğmesi kamerayı KENDİ açıyor ve
                  çekilen kareyi yönlendirme durumunda taşıyor. Burada onu
                  alıp `initialPhoto` olarak veriyoruz.

                  ScanCatPhotoPicker'a DOKUNULMADI: bileşen `initialPhoto`
                  prop'unu ve onu uygulayan etkiyi zaten taşıyordu, yalnızca
                  kimse geçmiyordu. O dosya hem aramayı hem kayıt sihirbazını
                  taşıyor ve 31 Tem'de ona yapılan iki müdahale de geri
                  alındı; bağlantıyı dışarıdan kurmak onu riske sokmuyor. */}
              <Route path="/scan-cat" element={<ScanCatRotasi />} />
              {/* Yeni arama akışının KABUĞU (1 Ağu 2026).
                  Hiçbir yerden bağlantı verilmiyor — yalnızca adresi elle
                  açan görür. `/scan-cat` ve alt menü hiç değişmedi, yani
                  bu rota canlıya çıksa bile kullanıcı davranışı aynı.
                  Kabuk onaylanınca yapay zeka düğmesi buraya çevrilecek;
                  geri dönüş de aynı tek satır. */}
              <Route path="/kedi-kimligi" element={<KediKimligi />} />
              <Route path="/aci-etiketle" element={<AciEtiketle />} />
              <Route path="/kedi-profili-olustur" element={<KediProfiliOlustur />} />
              <Route path="/map" element={<MapView />} />
              <Route path="/sightings" element={<Sightings />} />
              <Route path="/emergencies" element={<Sightings />} />
              <Route path="/emergencies/:id" element={<EmergencyDetail />} />
              <Route path="/emergency-report" element={<EmergencyReport />} />
              <Route path="/odul-avcisi" element={<OdulAvcisi />} />
              <Route path="/volunteer-call" element={<VolunteerCall />} />
              <Route path="/food-water-call" element={<FoodWaterCall />} />
              <Route path="/food-water-calls/:id" element={<FoodWaterCallDetail />} />
              {/* ACIL-02 (8 Eyl 2026): `/emergency-feed`in dort turunden UCUNUN
                  detay rotasi YOKTU; kart haritaya dusuyordu. Ucu de tek bilesen
                  ciziyor (`AcilKayitDetay`), tur yoldan cozuluyor. Beyaz liste
                  `lib/onemliVakalar.js` -> VAR_OLAN_DETAY_ROTALARI ile ESLESIR. */}
              {/* ACİL-04: kaydedilen vakalar. Kalp bu ekran olmadan
                  yarım kalırdı — basılır, gideceği yer olmaz. */}
              <Route path="/kaydettiklerim" element={<Kaydettiklerim />} />
              <Route path="/injured-reports/:id" element={<AcilKayitDetay />} />
              <Route path="/danger-zones/:id" element={<AcilKayitDetay />} />
              <Route path="/volunteer-calls/:id" element={<AcilKayitDetay />} />
              <Route path="/injured-cat" element={<InjuredCat />} />
              <Route path="/danger-zone" element={<DangerZone />} />
              <Route path="/volunteers" element={<Volunteers />} />
              {/* GECICI — PLAN-07 M-A. Hicbir yerden baglantisi YOK;
                  alt bar artik /mesajlar. /messages/:id de yeni Ekran 2ye gidiyor.
                  Bu sayfa alt bara baglandigi COMMITTE Messenger.jsx silinir. */}
              <Route path="/mesajlar" element={<MesajlarAna />} />
              <Route path="/mesajlar/istekler" element={<MesajIstekleri />} />
              {/* GECICI — PLAN-07 M-C. Baglantisiz. `/mesajlar/istekler` ile
                  cakismasin diye :id rotasi ONUN ALTINDA duruyor. */}
              <Route path="/mesajlar/:id" element={<KonusmaDetay />} />
              <Route path="/mesajlar/:id/konum" element={<KonumPaylas />} />
              <Route path="/mesajlar/:id/medya" element={<MedyaPaylas />} />
              <Route path="/messages" element={<Messenger />} />
              <Route path="/messages/:id" element={<KonusmaDetay />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/feeding" element={<Feeding />} />
              <Route path="/feeding/new" element={<AddFeeding />} />
              <Route path="/feeding/:id" element={<FeedingDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<ErrorBoundary scope="profile"><Profile /></ErrorBoundary>} />
              {/* The "Düzenle" button on the profile has pointed here all along.
                  The route did not exist, so it opened a blank page. */}
              <Route path="/profile/edit" element={<ErrorBoundary scope="profile-edit"><ProfileEdit /></ErrorBoundary>} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/premium" element={premiumGizli() ? <Navigate to="/" replace /> : <Premium />} />
              <Route path="/premium/success" element={premiumGizli() ? <Navigate to="/" replace /> : <PremiumSuccess />} />
              <Route path="/premium/cancel" element={premiumGizli() ? <Navigate to="/" replace /> : <PremiumCancel />} />
              <Route path="/premium/lounge" element={premiumGizli() ? <Navigate to="/" replace /> : <PremiumLounge />} />
              <Route path="/apply-volunteer" element={<ApplyVolunteer />} />
              <Route path="/admin/applications" element={<AdminApplications />} />
              <Route path="/admin/emergencies" element={<AdminEmergencies />} />
              <Route path="/photos/monthly" element={<PhotoMonthly />} />
              <Route path="/badges" element={<Badges />} />
              {/* GÜVENLIK-1: rota da kapali. Bagi gizlemek yetmez — paylasilmis
                  bir adres kapiyi deler. Turkiye disinda sayfa YOK sayilir. */}
              {guvenlikSayfasiAcikMi() && (
                <Route path="/safety" element={<CatSafety />} />
              )}
              <Route path="/observation-house" element={<ObservationHouse />} />
              {/* Mahallem = analiz/rapor sayfası (27 Tem 2026). Eski akışlı
                  sayfa tamamen kaldırıldı; paylaşımlar Pati Ağı'nda,
                  paylaşım kartı components/PostCard.jsx'te yaşıyor. */}
              <Route path="/neighborhood" element={<MahalleRapor />} />
              <Route path="/pati-agi" element={<PatiAgi />} />
              <Route path="/mahalle-rapor" element={<MahalleRapor />} />
              <Route path="/gorevler" element={<Gorevler />} />
              <Route path="/gorevler/liste" element={<GorevListesi />} />
              <Route path="/gorevler/yeni" element={<GorevYeni />} />
              {/* Sabit parça, /gorevler/:id'den önce okunur — React Router v6
                  eşleşmeyi sıraya değil özgüllüğe göre yapar ama okuyan insan
                  için burada durması daha anlaşılır. */}
              <Route path="/gorevler/istatistik" element={<AktifGorevler />} />
              <Route path="/gorevler/:id" element={<GorevDetay />} />
              <Route path="/gorevler/:id/sohbet" element={<GorevSohbet />} />
              <Route path="/gorevler/:id/etki" element={<GorevEtki />} />
              <Route path="/gruplar" element={<Gruplar />} />
              <Route path="/gruplar/yeni" element={<GrupYeni />} />
              <Route path="/gruplar/:id" element={<GrupDetay />} />
              <Route path="/gruplar/:id/sohbet" element={<GrupSohbeti />} />
              <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* App Review, Guideline 1.2: a reviewer must be able to reach these.
                  The pages existed and were unreachable, which is the same as not
                  existing — the last rejection was for exactly this. */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/community-rules" element={<CommunityRules />} />
              <Route path="/u/:userId" element={<UserProfile />} />
              <Route path="/pati-arkadaslarim" element={<PawFriends />} />
              {/* EKRAN 5 — panodaki "Tanittigin Kediler". /cats GENEL listedir,
                  sahip suzgeci yoktur; kullanici kendi kedilerinin tam listesini
                  bu rota acilana kadar hicbir yerden goremiyordu. */}
              <Route path="/kedilerim" element={<TanittiginKediler />} />
              {/* EKRAN 2 — panodaki "Katkilarim". Profil ana ekranindaki dort
                  karodan birincisi buraya bakiyor; rota unutulunca karo bos
                  sayfa aciyordu (6 Eyl aksami yakalandi). */}
              <Route path="/katkilarim" element={<Katkilarim />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              {/* Three links sat on the profile page pointing at nothing.
                  A dead link is not a missing page — it is a broken app. */}
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/help" element={<Help />} />
              <Route path="/about" element={<About />} />
              {/* Evcil Kedilerim — a person's own cats, kept out of the street
                  registry. Reached from a card on the profile. */}
              <Route path="/evcil-kedilerim" element={<MyPetCats />} />
              <Route path="/evcil-kedilerim/:id" element={<MyPetCatDetail />} />
              <Route path="/incidents/:id/feedback" element={<IncidentFeedback />} />
              {/* Catch-all, and it must stay LAST. Without it React Router
                  matched nothing and rendered nothing: a white page under the
                  navigation bar. Every mistyped URL and every dead link landed
                  there in silence. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </Layout>
          </ErrorBoundary>
          {/* BILDIRIM-1 (1 Eyl 2026) — kullanici sikayeti: "gonderildi / yapildi"
              yazilari gorunmuyor, daha gorunur olmali.
              Yer YUKARIDA kaliyor (kullanici boyle istedi) ama artik sag ust
              kosede kucuk bir kutu degil: ust bar'in (h-16 = 64px) hemen altinda,
              telefonda TAM GENISLIK bir serit. Kucuk kose kutusu goz ucuyla
              kaciriliyordu; genis serit kacirilmaz.
              env(safe-area-inset-top): centikli/dinamik adali telefonlarda ust
              bar asagi kayiyor, serit de onunla birlikte kaysin diye. */}
          <Toaster
            position="top-center"
            richColors
            duration={5000}
            closeButton
            offset={{ top: "76px" }}
            mobileOffset={{ top: "calc(76px + env(safe-area-inset-top))", left: "10px", right: "10px" }}
            toastOptions={{
              classNames: {
                toast: "!w-full !text-[17px] !font-bold !py-5 !px-4 !gap-3 !rounded-2xl !shadow-[0_20px_50px_-8px_rgba(0,0,0,0.5)] !border-2",
                title: "!text-[17px] !font-bold !leading-snug",
                description: "!text-[14px] !font-medium",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
      <UlkeKapisi />
    </I18nProvider>
  );
}

export default App;
